export type ApiError = { status: number; message: string };

export async function api<T = unknown>(
  path: string,
  opts: RequestInit & { method?: string; json?: unknown } = {}
): Promise<T> {
  const { json, headers, ...rest } = opts;
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(headers as Record<string, string> | undefined),
  };
  let body: BodyInit | undefined = rest.body ?? undefined;
  if (json !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
    body = JSON.stringify(json);
  }
  const res = await fetch(path, {
    ...rest,
    body,
    credentials: "include",
    headers: finalHeaders,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail =
      (data && typeof data === "object" && ((data as any).detail ?? (data as any).error)) ||
      (typeof data === "string" ? data : `HTTP ${res.status}`);
    throw { status: res.status, message: normalizeDetail(detail) } as ApiError;
  }
  return data as T;
}

function normalizeDetail(detail: unknown): string {
  // FastAPI validation errors come through as an array of {loc, msg, type}
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => (typeof d === "string" ? d : d?.msg ?? JSON.stringify(d)))
      .join("; ");
  }
  if (typeof detail === "string") return detail;
  return JSON.stringify(detail);
}
